
import React from 'react';
import { SimulationConfig, CollisionEffectType, CollisionColorMode } from './types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';

interface ImpactFXSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const ImpactFXSection: React.FC<ImpactFXSectionProps> = ({ config, setConfig }) => {
  const effectCategories = [
    { name: 'Physics Based', effects: ['RIPPLE', 'GRAVITY_PARTICLES', 'LIQUID_SPLASH', 'SPARKS', 'SHATTER_GLASS', 'PIXEL_CRUMBLE', 'SMOKE_PUFF', 'INK_SPLAT', 'CRYSTAL_SHARDS'] },
    { name: 'Energetic & Sci-Fi', effects: ['ORBITAL_SPIN', 'ELASTIC_RING', 'NEON_PULSE', 'VORTEX_SUCK', 'FIREWORKS', 'LIGHTNING_BOLT', 'SONIC_WAVE', 'QUANTUM_TUNNEL', 'PLASMA_BLOB', 'SUPERNOVA'] },
    { name: 'Abstract & Style', effects: ['GLITCH_FRAGMENTS', 'BUBBLE_POP', 'SPIRAL_GALAXY', 'DNA_HELIX', 'MATRIX_RAIN', 'GHOST_SOULS', 'CYBER_HEX', 'CONFETTI_CANNON'] }
  ];

  const colorModes: CollisionColorMode[] = ['BALL_COLOR', 'RAINBOW', 'CUSTOM_SOLID', 'CUSTOM_GRADIENT'];

  const ColorInput = ({ label, value, onChange, settingKey }: { label: string, value: string, onChange: (v: string) => void, settingKey?: string }) => (
    <ControlGroup label={label} settingKey={settingKey} darkMode={config.darkMode}>
        <div className="flex items-center gap-2 border border-white/5 p-2 rounded-xl">
            <div className="w-6 h-6 rounded-full border border-white/20 shadow-sm" style={{ backgroundColor: value }} />
            <div className="relative flex-1">
                 <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                 <input type="text" value={value} onChange={e => onChange(e.target.value)} className={`w-full px-2 py-1 text-[9px] font-mono border rounded-lg ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`} />
            </div>
        </div>
    </ControlGroup>
  );

  return (
    <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border-2 bg-pink-500/10 border-pink-500/30 mb-4 shadow-lg shadow-pink-500/5">
      <div className="flex items-center justify-between">
          <span className="text-[10px] font-black uppercase text-pink-400">Impact Visuals</span>
          <span className="bg-pink-600 text-white text-[8px] font-black uppercase px-2 py-0.5 rounded-full animate-pulse">New!</span>
      </div>
      
      <ControlGroup label="Select Effect Style" settingKey="collisionEffect" darkMode={config.darkMode}>
        <select 
            value={config.collisionEffect || 'RIPPLE'} 
            onChange={e => setConfig(p => ({...p, collisionEffect: e.target.value as CollisionEffectType}))}
            className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
        >
            {effectCategories.map(cat => (
                <optgroup key={cat.name} label={cat.name} className="bg-neutral-900 text-white font-bold italic">
                    {cat.effects.map(e => <option key={e} value={e} className="bg-neutral-900 text-white font-normal not-italic">{e.replace('_', ' ')}</option>)}
                </optgroup>
            ))}
        </select>
      </ControlGroup>

      <div className="flex flex-col gap-1">
          <SmartInput label="Duration (ms)" min={100} max={2000} value={config.collisionEffectDuration || 800} onChange={v => setConfig(p => ({...p, collisionEffectDuration: v}))} darkMode={config.darkMode} settingKey="collisionEffectDuration" fullWidth />
          <SmartInput label="Particles" min={1} max={100} value={config.collisionEffectParticleCount || 20} onChange={v => setConfig(p => ({...p, collisionEffectParticleCount: v}))} darkMode={config.darkMode} settingKey="collisionEffectParticleCount" fullWidth />
          <SmartInput label="Scale" min={0.1} max={5.0} step={0.1} value={config.collisionEffectScale || 1.0} onChange={v => setConfig(p => ({...p, collisionEffectScale: v}))} darkMode={config.darkMode} settingKey="collisionEffectScale" fullWidth />
      </div>
      <SmartInput label="Gravity" min={0} max={2} step={0.1} value={config.collisionEffectGravity ?? 0.5} onChange={v => setConfig(p => ({...p, collisionEffectGravity: v}))} darkMode={config.darkMode} settingKey="collisionEffectGravity" fullWidth />

      <div className="h-px bg-white/10 my-1" />
      <span className="text-[9px] font-black uppercase opacity-60">Effect Coloring</span>
      
      <ControlGroup label="Color Mode" settingKey="collisionEffectColorMode" darkMode={config.darkMode}>
        <select 
            value={config.collisionEffectColorMode || 'BALL_COLOR'} 
            onChange={e => setConfig(p => ({...p, collisionEffectColorMode: e.target.value as CollisionColorMode}))}
            className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
        >
            {colorModes.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
        </select>
      </ControlGroup>

      {(config.collisionEffectColorMode === 'CUSTOM_SOLID' || config.collisionEffectColorMode === 'CUSTOM_GRADIENT') && (
          <ColorInput label="Primary Color" value={config.collisionEffectColor1 || '#ff0000'} onChange={v => setConfig(p => ({...p, collisionEffectColor1: v}))} settingKey="collisionEffectColor1" />
      )}
      
      {config.collisionEffectColorMode === 'CUSTOM_GRADIENT' && (
          <ColorInput label="Secondary Color" value={config.collisionEffectColor2 || '#ffff00'} onChange={v => setConfig(p => ({...p, collisionEffectColor2: v}))} settingKey="collisionEffectColor2" />
      )}
    </div>
  );
};
